import { useToast } from '@/composables/useToast';
import { endpointsList } from '@/helpers';
import axios, { HttpStatusCode, isAxiosError } from 'axios';
import {
  IAddWorkspaceReadOnlyDocumentsDto,
  ICreateWorkspaceDocumentWorkingCopiesDto,
  IWorkspaceDocumentSearchResultItemDto,
  WorkspaceDocumentPickerMode,
} from 'types';
import { useI18n } from 'vue-i18n';

type WorkspaceDocumentsSelectionPayload = {
  mode: WorkspaceDocumentPickerMode;
  documents: IWorkspaceDocumentSearchResultItemDto[];
};

type UseWorkspaceDocumentSelectionOptions = {
  onReadOnlyAdded?: () => Promise<void> | void;
  onWorkingCopiesCreated?: () => Promise<void> | void;
};

export function useWorkspaceDocumentSelection(
  workspaceId: string,
  options?: UseWorkspaceDocumentSelectionOptions,
) {
  const { t } = useI18n();
  const { createErrorToast, createSuccessToast } = useToast();

  async function handleDocumentsSelected(
    payload: WorkspaceDocumentsSelectionPayload,
  ): Promise<boolean> {
    const documentIds = [
      ...new Set(payload.documents.map((document) => document._id)),
    ];

    if (payload.mode === WorkspaceDocumentPickerMode.EDIT) {
      return await handleEditDocumentsSelected(documentIds);
    } else {
      return await handleReadonlyDocumentsSelected(documentIds);
    }
  }

  async function handleEditDocumentsSelected(
    documentIds: string[],
  ): Promise<boolean> {
    const requestBody: ICreateWorkspaceDocumentWorkingCopiesDto = {
      documentIds,
    };
    try {
      await axios.post(
        endpointsList.workspaceUploads.createWorkingCopies(workspaceId),
        requestBody,
      );
      createSuccessToast(
        t('workspaceDocuments.picker.workingCopiesCreated', {
          count: documentIds.length,
        }),
      );
      if (options?.onWorkingCopiesCreated) {
        await options.onWorkingCopiesCreated();
      }
      return true;
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === HttpStatusCode.Forbidden) {
        createErrorToast(t('workspaceDocuments.picker.errors.kksNotInScope'));
        return false;
      }
      createErrorToast(
        t('workspaceDocuments.picker.errors.createWorkingCopiesFailed'),
      );
      return false;
    }
  }

  async function handleReadonlyDocumentsSelected(
    documentIds: string[],
  ): Promise<boolean> {
    const requestBody: IAddWorkspaceReadOnlyDocumentsDto = {
      documentIds,
    };
    try {
      await axios.post(
        endpointsList.workspaceUploads.addReadOnlyDocuments(workspaceId),
        requestBody,
      );
      createSuccessToast(
        t('workspaceDocuments.picker.readOnlyAdded', {
          count: documentIds.length,
        }),
      );
      if (options?.onReadOnlyAdded) {
        await options.onReadOnlyAdded();
      }
      return true;
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === HttpStatusCode.Forbidden) {
        createErrorToast(t('workspaceDocuments.picker.errors.kksNotInScope'));
        return false;
      }
      createErrorToast(t('workspaceDocuments.picker.errors.addReadOnlyFailed'));
      return false;
    }
  }

  return {
    handleDocumentsSelected,
  };
}
